/**
 * The campaign list.
 *
 * Two things it does that a plain CRUD table would not:
 *
 *  - Pause and resume go through their own narrow endpoint, never through the
 *    editor's PATCH. The reference implementation this module was measured
 *    against puts both behind one PUT, and its list page calls that PUT with
 *    empty arrays - so pausing a campaign wipes its contents. Here the toggle
 *    cannot carry a body that deletes anything.
 *  - The transport warning sits above the list rather than in a doc nobody
 *    opens. Our production mail transport's own terms forbid cold outreach, so
 *    until a separate sending domain exists, "active" means queued, not sent.
 *
 * The rows are the CRM's pills. The deeper campaign panels still draw from this
 * module's own `shared.tsx` vocabulary, which is a coherent set of its own and
 * is left alone here.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronRight, Loader2, Megaphone, Plus, Users } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import {
  deleteCampaign, listCampaigns, setCampaignStatus,
  type CampaignRow, type CampaignStatus,
} from "../../../lib/marketing";
import { Btn, EmptyState, PageHead, StatusPill, Toolbar, ToolbarGap, fmtNum, fmtStamp, listCard } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { CampaignRowMenu } from "./CampaignRowMenu";
import { NewCampaignModal } from "./NewCampaignModal";
import { Note } from "./shared";
import { campaignTone } from "./status";
import { contactedTotal } from "./lib";

/**
 * Name, audience, sent, skipped, replied, updated - six tracks plus the actions
 * column, used by both the head and the rows so a new column cannot shift one
 * without the other.
 *
 * The name track is wide because it carries three things, not one: the campaign
 * name, its status pill and the "automatic" chip. Sized off the name alone it
 * truncated at about twelve characters.
 */
const COLS = "minmax(0,2.9fr) 1.05fr .55fr .55fr .55fr .8fr 44px";

export function CampaignsList({ user }: { user: StaffUser }) {
  const t = useT();
  const navigate = useNavigate();
  const canManage = userCanPlatform(user, "manage:crm.marketing");

  const [rows, setRows] = useState<CampaignRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [confirmDel, setConfirmDel] = useState<CampaignRow | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await listCampaigns();
      setRows(res.rows);
      setError(null);
    } catch {
      setRows([]);
      setError(t("marketing.error.load"));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(row: CampaignRow) {
    const next: CampaignStatus = row.status === "active" ? "paused" : "active";
    setBusy(row.id);
    try {
      const res = await setCampaignStatus(row.id, next);
      setRows((prev) => (prev ?? []).map((r) => (r.id === row.id ? res.campaign : r)));
      setError(null);
    } catch (e) {
      // 422 means the campaign is missing something it needs before it can run.
      // Saying which one is the difference between a fixable message and a shrug.
      const missing = (e as { body?: { missing?: string[] } })?.body?.missing;
      setError(
        missing?.length
          ? t("marketing.error.incomplete", { fields: missing.map((f) => t(`marketing.field.${f}`)).join(", ") })
          : t("marketing.error.status"),
      );
    } finally {
      setBusy(null);
    }
  }

  async function remove(row: CampaignRow) {
    setBusy(row.id);
    try {
      await deleteCampaign(row.id);
      setRows((prev) => (prev ?? []).filter((r) => r.id !== row.id));
      setError(null);
    } catch {
      setError(t("marketing.error.deleteSent"));
    } finally {
      setBusy(null);
      setConfirmDel(null);
    }
  }

  return (
    // One open menu at a time across every row.
    <PopHost>
      <PageHead
        title={t("marketing.title")}
        sub={t("marketing.intro")}
        actions={
          <>
            {/* The two sender-wide pages. Neither is a sidebar row any more:
                both are about the sending as a whole rather than about one
                campaign, and this list is the page somebody is already on when
                the question becomes "why is nothing going out" or "make it
                stop". Automation first, because it is the one with a switch. */}
            <Link to="/marketing/automation" className="crm-btn">
              {t("nav.marketingAutomation")}
              <ChevronRight className="size-4" />
            </Link>
            <Link to="/marketing/suppression" className="crm-btn">
              {t("nav.suppression")}
              <ChevronRight className="size-4" />
            </Link>
            {canManage ? (
              <Btn tone="brand" onClick={() => setShowNew(true)}>
                <Plus className="size-4" />
                {t("marketing.new")}
              </Btn>
            ) : null}
          </>
        }
      />

      {/* Not decoration. Our transport's acceptable-use policy forbids cold
          outreach, and running it through the same account as password resets
          and invoices puts those at risk. See docs/crm-turundus-plaan.md. */}
      <Note tone="warn" icon={<AlertTriangle className="size-3.5" />} className="mb-3.5">
        {t("marketing.transportWarning")}
      </Note>

      {error ? (
        <Note tone="bad" icon={<AlertTriangle className="size-3.5" />} className="mb-3.5">
          {error}
        </Note>
      ) : null}

      <Toolbar>
        <span className="crm-sub px-1.5">
          {rows ? t("marketing.count", { n: rows.length }) : t("common.loading")}
        </span>
        <ToolbarGap />
      </Toolbar>

      <div className="crm-card" style={listCard(COLS)}>
        {rows === null ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Megaphone}
            title={t("marketing.empty")}
            body={t("marketing.emptyBody")}
            action={
              canManage ? (
                <Btn tone="brand" onClick={() => setShowNew(true)}>
                  <Plus className="size-4" />
                  {t("marketing.new")}
                </Btn>
              ) : null
            }
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
              <span>{t("marketing.col.name")}</span>
              <span>{t("marketing.col.audience")}</span>
              <span>{t("marketing.col.sent")}</span>
              <span>{t("marketing.col.skipped")}</span>
              <span>{t("marketing.col.replied")}</span>
              <span>{t("marketing.col.updated")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {rows.map((r) => (
                <div key={r.id} className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
                  <span className="min-w-0 pl-3">
                    <span className="flex min-w-0 items-center gap-2">
                      <Link
                        to={`/marketing/campaigns/${r.id}`}
                        className="crm-prow-n crm-trunc min-w-0 after:absolute after:inset-0 after:rounded-full"
                      >
                        {r.name}
                      </Link>
                      <StatusPill tone={campaignTone(r.status)}>{t(`marketing.status.${r.status}`)}</StatusPill>
                      {r.autoEnabled ? <span className="crm-chip">{t("marketing.autoOn")}</span> : null}
                    </span>
                    <span className="crm-prow-s crm-tnum crm-trunc block">/{r.slug}</span>
                  </span>

                  <span className="crm-mut flex min-w-0 items-center gap-1.5 text-[12.5px]">
                    <Users className="size-3.5 shrink-0" />
                    <span className="crm-trunc">{t(`marketing.source.${r.audienceSource}`)}</span>
                    {r.audienceCount != null ? (
                      <span className="crm-tnum shrink-0 font-bold">{fmtNum(r.audienceCount)}</span>
                    ) : null}
                  </span>

                  {/* Sent is the figure that matters, so it carries the weight;
                      skipped and replied are context and stay quiet.

                      ⚠ SUMMED, not `r.sent`. A row holds one status and the
                      later fact overwrites the earlier one, so `sent` alone
                      means "went out and nothing has happened since" - it goes
                      DOWN as replies and bounces arrive, which is the opposite
                      of what this column claims to say. */}
                  <span className="crm-tnum text-[13.25px] font-bold">{fmtNum(contactedTotal(r))}</span>
                  <span className="crm-mut crm-tnum text-[13px]">{fmtNum(r.skipped)}</span>
                  <span className="crm-tnum text-[13px]">{fmtNum(r.replied)}</span>
                  <span className="crm-mut crm-tnum text-[12.5px]">{fmtStamp(r.updatedAt)}</span>

                  <CampaignRowMenu
                    row={r}
                    canManage={canManage}
                    busy={busy === r.id}
                    onToggle={() => void toggle(r)}
                    onDelete={() => setConfirmDel(r)}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {showNew ? (
        <NewCampaignModal
          onClose={() => setShowNew(false)}
          onCreated={(id) => navigate(`/marketing/campaigns/${id}`)}
          onError={() => {
            setShowNew(false);
            setError(t("marketing.error.create"));
          }}
        />
      ) : null}

      {confirmDel ? (
        <ConfirmModal
          title={t("marketing.deleteTitle")}
          body={t("marketing.confirmDelete", { name: confirmDel.name })}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => remove(confirmDel)}
          onClose={() => setConfirmDel(null)}
        />
      ) : null}
    </PopHost>
  );
}
